import { FC } from 'react';
import { PresentationOutline } from '@/types/assistant';

interface Props {
  outline: PresentationOutline;
}

/** Ícone + rótulo PT por layout canônico (D02/D03). Desconhecido → tópicos. */
const LAYOUT_META: Record<string, { icon: string; label: string }> = {
  capa: { icon: 'title', label: 'Capa' },
  secao: { icon: 'segment', label: 'Seção' },
  topicos: { icon: 'format_list_bulleted', label: 'Tópicos' },
  imagem: { icon: 'image', label: 'Imagem' },
  texto_imagem: { icon: 'view_sidebar', label: 'Texto + imagem' },
  comparacao: { icon: 'compare', label: 'Comparação' },
  citacao: { icon: 'format_quote', label: 'Citação' },
  dados: { icon: 'bar_chart', label: 'Dados' },
  encerramento: { icon: 'waving_hand', label: 'Encerramento' },
};

function meta(layout: string) {
  return LAYOUT_META[layout] ?? LAYOUT_META.topicos;
}

/**
 * Pré-visualização do roteiro (outline) do deck do H.Slide (Frente 6, D04). Renderiza
 * `Message.metadata.outline` (D03) como uma lista enumerada de slides (nº + layout +
 * título) — o usuário vê o esqueleto da apresentação e pode iterar ("troca o slide 3")
 * pelo chat normal. O .pptx em si vem como chip de download (kind=PPTX).
 */
const OutlinePreview: FC<Props> = ({ outline }) => {
  const slides = outline.slides ?? [];
  if (slides.length === 0) return null;
  return (
    <div data-testid="outline-preview" className="mt-3 pt-3 border-t border-surface-border">
      <div className="flex items-center gap-2 mb-2">
        <span className="material-symbols-outlined text-accent text-lg leading-none">slideshow</span>
        <span className="text-sm font-semibold text-content-primary">
          Roteiro{outline.title ? `: ${outline.title}` : ''}
        </span>
        <span className="text-2xs text-content-tertiary">· {slides.length} slides</span>
      </div>
      <ol className="rounded-lg bg-surface-secondary border border-surface-border divide-y divide-surface-border">
        {slides.map((s, i) => {
          const m = meta(s.layout);
          return (
            <li
              key={i}
              data-testid="outline-slide"
              data-layout={s.layout}
              className="flex items-start gap-2.5 px-3 py-2"
            >
              <span className="shrink-0 text-2xs font-semibold text-content-tertiary w-5 text-right tabular-nums mt-0.5">
                {i + 1}
              </span>
              <span
                className="material-symbols-outlined text-base leading-none text-content-tertiary mt-0.5 shrink-0"
                title={m.label}
                aria-hidden
              >
                {m.icon}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm text-content-primary break-words">
                  {s.heading || <span className="text-content-tertiary italic">(sem título)</span>}
                </p>
                <span className="text-2xs text-content-tertiary">{m.label}</span>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
};

export default OutlinePreview;
